import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import classNames from 'classnames';

import useApi from 'common/useApi';
import UiContext from 'common/UiContext/UiContext';
import AuthContext from 'common/auth';
import getHashrateValueWithUnit from 'common/getHashrateValueWithUnit';
import {
  RANGES,
  RANGE_CONFIG,
  RANGE_LABELS,
  HashrateRange,
  buildTimeTicks,
} from 'common/timeAxis';
import displayHashrateValue from 'common/displayHashrateValue';

import HashrateGraph from 'components/HashrateGraph';
import Loading from 'components/Loading';
import WorkerStateLabelBadge from 'components/WorkerStateLabelBadge';

type WorkerGraphPoint = {
  date: string;
  hashrate: string;
  scoring_hashrate: string;
};

const WorkerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useContext(UiContext);
  const { user } = useContext(AuthContext)!;
  const timezone = user?.timezone;

  const [range, setRange] = useState<HashrateRange>('24h');

  const { data: worker, isLoading: workerLoading, isError: workerError } = useApi(
    'get',
    '/api/workers',
    id,
  );

  const { data: graphData, isError: graphError, isLoading: graphLoading, isFetching } = useApi(
    'getList',
    `/api/workers/${id}/graph`,
    { range },
  );

  useEffect(() => {
    if (workerError || graphError) addToast!('Error loading worker data');
  }, [workerError, graphError]);

  const hashrates: (WorkerGraphPoint & { workers_active: number })[] = useMemo(
    () => (graphData ?? []).map((p: WorkerGraphPoint) => ({ ...p, workers_active: 0 })),
    [graphData],
  );

  const { startMs, endMs, bucketMs } = useMemo(() => {
    const end = Date.now();
    const cfg = RANGE_CONFIG[range];

    return { startMs: end - cfg.windowMs, endMs: end, bucketMs: cfg.bucketMs };
  }, [range, graphData]);

  const ticks = useMemo(
    () => buildTimeTicks(startMs, endMs, range, timezone),
    [startMs, endMs, range, timezone],
  );

  const hashrateUnits = useMemo(() => {
    const values = hashrates.flatMap(({ hashrate, scoring_hashrate }) => [
      parseFloat(hashrate),
      parseFloat(scoring_hashrate),
    ]);

    return getHashrateValueWithUnit(Math.max(1, ...values));
  }, [hashrates]);

  const max = useMemo(() => {
    let m = 1;

    hashrates.forEach(({ hashrate, scoring_hashrate }) => {
      if (parseFloat(hashrate) > m) m = parseFloat(hashrate);
      if (parseFloat(scoring_hashrate) > m) m = parseFloat(scoring_hashrate);
    });
    const mag = Math.pow(10, Math.floor(Math.log10(m)));

    return Math.ceil(m / mag) * mag;
  }, [hashrates]);

  const steps = useMemo(() => {
    const s = parseInt(max.toString()[0]);

    return isNaN(s) || s === 0 ? 1 : s;
  }, [max]);

  const hashrateLabel = (i: number) => {
    const val = (max / steps) * i;
    const u = getHashrateValueWithUnit(val);

    return val / u.coefficient + u.unit;
  };

  if (workerLoading) return <Loading />;

  const fmt = (v: string) =>
    `${Object.values(displayHashrateValue(parseFloat(v))).join(' ')}h/s`;

  return (
    <>
      <div className='title-box' style={{ marginBottom: '1rem' }}>
        <Link to='/workers' style={{ opacity: 0.6, fontSize: '0.9em' }}>← Workers</Link>
      </div>

      {worker && (
        <article className='box' style={{ marginBottom: '1rem' }}>
          <h5 style={{ marginBottom: '0.75rem' }}>{worker.name || '(auto)'}</h5>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.9em' }}>
            {worker.account_name && (
              <div>
                <div style={{ opacity: 0.6 }}>Subaccount</div>
                <div>{worker.account_name}</div>
              </div>
            )}
            <div>
              <div style={{ opacity: 0.6 }}>Status</div>
              <WorkerStateLabelBadge state={worker.status} />
            </div>
            <div>
              <div style={{ opacity: 0.6 }}>Hashrate (5m)</div>
              <div>{fmt(worker.hashrate)}</div>
            </div>
            <div>
              <div style={{ opacity: 0.6 }}>Hashrate (1h)</div>
              <div>{fmt(worker.hashrate_1h)}</div>
            </div>
            <div>
              <div style={{ opacity: 0.6 }}>Scoring HR</div>
              <div>{fmt(worker.scoring_hashrate)}</div>
            </div>
            {worker.label && (
              <div>
                <div style={{ opacity: 0.6 }}>Label</div>
                <div>{worker.label}</div>
              </div>
            )}
          </div>
        </article>
      )}

      <div className={classNames({ box: true, isLoading: graphLoading || isFetching })}>
        <h5>Hashrate History</h5>
        <nav className='boxNavigation'>
          <div className='rangeSelector'>
            {RANGES.map((opt) => (
              <button
                key={opt}
                type='button'
                className={classNames({ active: opt === range })}
                onClick={() => setRange(opt)}
              >
                {RANGE_LABELS[opt]}
              </button>
            ))}
          </div>
        </nav>
        <div className='hashrate-graph-scaffolding'>
          <div className='unit-names'>
            <span>Hashrate</span>
            <span />
          </div>
          <div className='hashrate-values'>
            {hashrates.length > 0 && steps > 0 && (
              [...Array(steps + 1).fill(true)].map((_, k) => (
                <span key={k}>{hashrateLabel(k)}</span>
              ))
            )}
          </div>
          <HashrateGraph
            isLoading={graphLoading || isFetching}
            hashrateMaximized={true}
            hashRatesSlice={hashrates}
            hashrateUnits={hashrateUnits}
            maxWorkers={0}
            min={0}
            max={max}
            graphVisibleElements={['hashrate', 'scoring_hashrate']}
            startMs={startMs}
            endMs={endMs}
            bucketMs={bucketMs}
            ticks={ticks}
            timezone={timezone}
          />
          <div className='count-values' />
          <div className='hours'>
            {ticks.map((tick, k) => (
              <span key={k} style={{ left: `${tick.fraction * 100}%` }}>{tick.label}</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.85em', opacity: 0.8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span style={{
                width: 12,
                height: 3,
                background: 'var(--color-hashrate, #4a9eff)',
                display: 'inline-block',
              }} />
              Hashrate
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span style={{
                width: 12,
                height: 3,
                background: 'var(--color-scoring, #ff9f4a)',
                display: 'inline-block',
                borderTop: '2px dashed var(--color-scoring, #ff9f4a)',
              }} />
              Scoring
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkerDetail;
